import { useState, useEffect, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Skeleton } from '@/components/ui/skeleton'
import { Search, Building2 } from 'lucide-react'
import { useAvailableClinicsForGroup } from '@/hooks/useClinicGroups'
import { clinicGroupService } from '@/services/clinic_group_service'
import { useAuth } from '@/hooks/useAuth'
import { toast } from 'sonner'
import { useDebounce } from '@/hooks/useDebounce'

interface AssignClinicDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  groupId: number
  onSuccess: () => void
}

export function AssignClinicDialog({
  open,
  onOpenChange,
  groupId,
  onSuccess,
}: AssignClinicDialogProps) {
  const { token } = useAuth()
  const [search, setSearch] = useState('')
  const [selectedClinicIds, setSelectedClinicIds] = useState<number[]>([])
  const [isAssigning, setIsAssigning] = useState(false)

  const debouncedSearch = useDebounce(search, 300)

  const { clinics, isLoading } = useAvailableClinicsForGroup(open ? groupId : null, {
    search: debouncedSearch || undefined,
    pageSize: 999,
  })

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      setSearch('')
      setSelectedClinicIds([])
    }
  }, [open])

  const handleSelectClinic = (clinicId: number, checked: boolean) => {
    if (checked) {
      setSelectedClinicIds((prev) => [...prev, clinicId])
    } else {
      setSelectedClinicIds((prev) => prev.filter((id) => id !== clinicId))
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked && clinics) {
      setSelectedClinicIds(clinics.map((c) => c.clinic_id))
    } else {
      setSelectedClinicIds([])
    }
  }

  const isAllSelected = useMemo(() => {
    return clinics && clinics.length > 0 && selectedClinicIds.length === clinics.length
  }, [clinics, selectedClinicIds])

  const handleAssign = async () => {
    if (!token || selectedClinicIds.length === 0) return

    setIsAssigning(true)
    let successCount = 0
    let failCount = 0

    for (const clinicId of selectedClinicIds) {
      const result = await clinicGroupService.assignClinic(groupId, clinicId, token)
      if (result.success) {
        successCount++
      } else {
        failCount++
      }
    }

    setIsAssigning(false)

    if (successCount > 0) {
      toast.success(`成功指派 ${successCount} 間診所`)
      onSuccess()
      onOpenChange(false)
    }

    if (failCount > 0) {
      toast.error(`${failCount} 間診所指派失敗`)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>指派診所到集團</DialogTitle>
          <DialogDescription>選擇要指派到此集團的診所（可多選）</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="搜尋診所名稱..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Clinic list */}
          <div className="border rounded-md overflow-hidden">
            {/* Header with select all */}
            {clinics && clinics.length > 0 && (
              <div className="flex items-center gap-3 p-3 border-b bg-muted/50">
                <Checkbox
                  id="select-all"
                  checked={isAllSelected}
                  onCheckedChange={handleSelectAll}
                />
                <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                  全選
                </label>
                <span className="text-sm text-muted-foreground ml-auto truncate">
                  可指派的診所（尚未歸屬其他集團）
                </span>
              </div>
            )}

            <ScrollArea className="h-[300px] w-full overflow-hidden">
              <div className="w-0 min-w-full overflow-hidden">
                {isLoading ? (
                  <div className="p-4 space-y-3">
                    {[...Array(5)].map((_, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <Skeleton className="h-4 w-4" />
                        <Skeleton className="h-4 flex-1" />
                      </div>
                    ))}
                  </div>
                ) : clinics && clinics.length > 0 ? (
                  <div className="divide-y">
                    {clinics.map((clinic) => (
                      <div
                        key={clinic.clinic_id}
                        className="flex items-center gap-3 p-3 hover:bg-muted/50 cursor-pointer overflow-hidden"
                        onClick={() =>
                          handleSelectClinic(
                            clinic.clinic_id,
                            !selectedClinicIds.includes(clinic.clinic_id),
                          )
                        }
                      >
                        <Checkbox
                          checked={selectedClinicIds.includes(clinic.clinic_id)}
                          onCheckedChange={(checked) =>
                            handleSelectClinic(clinic.clinic_id, checked as boolean)
                          }
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Building2 className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">{clinic.clinic_name}</div>
                          {clinic.address && (
                            <div className="text-sm text-muted-foreground truncate">
                              {clinic.address}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full py-8 text-muted-foreground">
                    <Building2 className="h-8 w-8 mb-2" />
                    <p>沒有可指派的診所</p>
                    <p className="text-sm">所有診所都已歸屬集團或不符合搜尋條件</p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Selected count */}
          <div className="text-sm text-muted-foreground">
            已選擇 <span className="font-medium text-foreground">{selectedClinicIds.length}</span>{' '}
            間診所
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            取消
          </Button>
          <Button
            type="button"
            onClick={handleAssign}
            disabled={selectedClinicIds.length === 0 || isAssigning}
          >
            {isAssigning ? '指派中...' : `確定指派 (${selectedClinicIds.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
